import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { eventsApi } from '../../lib/api';

export default function EventPublicPage({ slug }) {
  const [event, setEvent] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('event_pages')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();
      setEvent(data);
      if (data?.pitch_event_id) {
        try {
          const s = await eventsApi.getSponsors(data.pitch_event_id);
          setSponsors(s);
        } catch (_) { /* no sponsors yet */ }
      }
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-gray-400 text-sm">Loading...</div>
    </div>
  );

  if (!event) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Event Not Found</h1>
        <p className="text-gray-500">This event doesn't exist or hasn't been published yet.</p>
      </div>
    </div>
  );

  const formatDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
        {event.hero_image_url && (
          <div className="absolute inset-0 opacity-20">
            <img src={event.hero_image_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
          {event.logo_image_url && (
            <img src={event.logo_image_url} alt="" className="h-16 mx-auto mb-6" />
          )}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>
            {event.public_headline || event.event_name}
          </h1>
          {event.public_subheadline && (
            <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto">{event.public_subheadline}</p>
          )}
          {event.tagline && (
            <p className="text-lg text-amber-400 font-medium mb-8">{event.tagline}</p>
          )}
          <div className="flex items-center justify-center gap-6 text-gray-300 text-sm mb-10">
            {event.start_date && (
              <span>📅 {formatDate(event.start_date)}{event.end_date ? ` — ${formatDate(event.end_date)}` : ''}</span>
            )}
            {event.time_commitment && <span>⏱ {event.time_commitment}</span>}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`/event/${slug}/companies`}
              className="px-8 py-4 rounded-full text-base font-semibold text-white transition-all hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              I'm a Company
            </a>
            <a
              href={`/event/${slug}/inventors`}
              className="px-8 py-4 rounded-full text-base font-semibold text-white border-2 border-white/30 hover:bg-white/10 transition-all"
            >
              I'm an Inventor
            </a>
          </div>
        </div>
      </section>

      {/* How It Works */}
      {event.how_it_works?.length > 0 && event.how_it_works[0]?.title && (
        <section className="max-w-4xl mx-auto px-6 py-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {event.how_it_works.filter(s => s.title).map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-lg font-bold mx-auto mb-4">{i + 1}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Featured Inventors */}
      {event.featured_inventors?.length > 0 && event.featured_inventors[0]?.name && (
        <section className="bg-gray-50 py-20">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>Featured Creators</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {event.featured_inventors.filter(i => i.name).map((inv, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-sm text-center">
                  {inv.photo_url ? (
                    <img src={inv.photo_url} alt={inv.name} className="w-20 h-20 rounded-full object-cover mx-auto mb-4" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                      {inv.name.charAt(0)}
                    </div>
                  )}
                  <h3 className="font-semibold text-gray-900">{inv.name}</h3>
                  {inv.title && <p className="text-sm text-amber-600 mb-2">{inv.title}</p>}
                  {inv.bio && <p className="text-sm text-gray-600 leading-relaxed">{inv.bio}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Past Attendees */}
      {event.past_attendees?.length > 0 && event.past_attendees[0] && (
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>Past Attendees</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {event.past_attendees.filter(a => a).map((att, i) => (
              <span key={i} className="px-5 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700">{att}</span>
            ))}
          </div>
        </section>
      )}

      {/* About the Host */}
      {event.about_host && (
        <section className="bg-gray-50 py-20">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>About the Host</h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line text-center">{event.about_host}</p>
          </div>
        </section>
      )}

      {/* Our Sponsors */}
      {sponsors.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-10" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>Our Sponsors</h2>
            {/* Higher tier sponsors (larger, top) */}
            {sponsors.some(s => s.amount_cents >= 50000) && (
              <div className="flex flex-wrap justify-center items-center gap-8 mb-8">
                {sponsors.filter(s => s.amount_cents >= 50000).map((s, i) => (
                  <div key={i} className="text-center">
                    {s.logo_url ? (
                      <img src={s.logo_url} alt={s.company_name} className="h-16 max-w-[200px] object-contain mx-auto" />
                    ) : (
                      <div className="inline-flex items-center justify-center px-6 py-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                        <span className="text-lg font-bold text-gray-800">{s.company_name}</span>
                      </div>
                    )}
                    <p className="text-xs text-amber-600 font-semibold mt-2">{s.tier_name}</p>
                  </div>
                ))}
              </div>
            )}
            {/* Lower tier sponsors (smaller, below) */}
            {sponsors.some(s => s.amount_cents < 50000) && (
              <div className="flex flex-wrap justify-center items-center gap-6">
                {sponsors.filter(s => s.amount_cents < 50000).map((s, i) => (
                  <div key={i} className="text-center">
                    {s.logo_url ? (
                      <img src={s.logo_url} alt={s.company_name} className="h-10 max-w-[140px] object-contain mx-auto" />
                    ) : (
                      <div className="inline-flex items-center justify-center px-4 py-2 bg-white rounded-lg border border-gray-200">
                        <span className="text-sm font-semibold text-gray-700">{s.company_name}</span>
                      </div>
                    )}
                    <p className="text-[10px] text-gray-400 font-medium mt-1">{s.tier_name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* FAQ */}
      {event.faq?.length > 0 && event.faq[0]?.question && (
        <section className="max-w-3xl mx-auto px-6 py-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>Frequently Asked Questions</h2>
          <div className="space-y-4">
            {event.faq.filter(f => f.question).map((item, i) => (
              <div key={i} className="border border-gray-200 rounded-2xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{item.question}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 text-center">
        <p className="text-xs text-gray-400">
          Powered by <span className="font-semibold" style={{ color: '#c9a84c' }}>PlayVentr</span>
        </p>
      </footer>
    </div>
  );
}
